/**
 * Admin API client. The staff token (password + OTP, valid 8 hours) is kept in sessionStorage: it
 * survives a reload but not closing the tab. Any 401 ends the session (expired, or access removed).
 */

export const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
const KEY = "rr-staff-token";

export type StaffRole = "super_admin" | "admin" | "moderator" | "support";
export type Status = "draft" | "under_review" | "live" | "rejected" | "expired" | "deactivated" | "deleted";

export interface StaffMe {
  id: string;
  full_name: string | null;
  email: string | null;
  mobile: string | null;
  staff_role: StaffRole;
  modules: Module[]; // console sections they can open
  ad_modules: AdModule[] | null; // kinds of ads they handle; null: all
  can_approve: boolean;
  level?: Level | null; // the review level they check at (super admins: either)
}

/** Two-level review: level 1 checks, then another person checks at level 2 and publishes. */
export type Level = "l1" | "l2";
export const LEVEL_LABEL: Record<Level, string> = { l1: "Level 1", l2: "Level 2" };

/** The levels this person may check at: a super admin either, an approver their own one. */
export function myLevels(me: StaffMe | null | undefined): Level[] {
  if (!me || !me.can_approve) return [];
  if (me.staff_role === "super_admin") return ["l1", "l2"];
  return me.level ? [me.level] : [];
}

export interface StaffMember extends StaffMe {
  mobile_verified: boolean;
  email_verified: boolean;
  custom_modules: Module[] | null; // the super admin's narrowing; null: all the role allows
  set_password_link?: string | null; // only right after a new account is made
}

export type Module = "ads" | "reports" | "reviews" | "pros" | "boxes" | "home" | "orders" | "plans" | "coupons" | "locations" | "subscribers" | "analytics" | "settings" | "audit";
export type AdModule = "property" | "hall" | "service";

export const MODULE_LABEL: Record<Module, string> = {
  ads: "Ads",
  reports: "Reports",
  reviews: "Reviews",
  pros: "Builders & agents",
  boxes: "Home boxes",
  home: "Home page",
  orders: "Orders",
  plans: "Plans",
  coupons: "Coupons",
  locations: "Locations",
  subscribers: "Subscribers",
  analytics: "Analytics",
  settings: "Settings",
  audit: "Audit trail",
};
export const AD_MODULE_LABEL: Record<AdModule, string> = { property: "Property", hall: "Function halls", service: "Services" };
/** Sections each role can have at most (the API holds the same list). */
export const ROLE_MODULES: Record<StaffRole, Module[]> = {
  super_admin: Object.keys(MODULE_LABEL) as Module[],
  admin: Object.keys(MODULE_LABEL) as Module[],
  moderator: (Object.keys(MODULE_LABEL) as Module[]).filter((m) => !["subscribers", "analytics", "audit"].includes(m)),
  support: (Object.keys(MODULE_LABEL) as Module[]).filter((m) => !["subscribers", "analytics", "audit", "home"].includes(m)),
};

export interface ActivityItem {
  action: string;
  target_type: string;
  target_id: string;
  detail: Record<string, unknown>;
  created_at: string;
  ad: string | null;
  ad_id: string | null;
}

export interface Activity {
  member: StaffMember;
  days: number;
  counts: Record<string, number>;
  last_seen: string | null;
  items: ActivityItem[];
}

export interface Owner {
  id: string;
  full_name: string | null;
  mobile: string | null;
  email: string | null;
  role: string;
  created_at: string;
}

export interface QueueItem {
  id: string;
  public_id: string;
  title: string;
  form_key: string;
  module: "property" | "hall" | "service";
  status: Status;
  plan: string | null;
  price: number | null;
  city: string | null;
  locality: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  expires_at: string | null;
  owner: Owner;
  photos: number;
  edit_status?: "draft" | "under_review" | "rejected" | null;
  edit_submitted_at?: string | null;
  deleted_at?: string | null;
  deactivated_by?: "owner" | "staff" | "account" | null;
  check_l1_at?: string | null; // level 1 done: waits for level 2
  check_l1_by?: string | null;
  check_l1_name?: string | null;
  delete_reason?: string | null;
}

export interface MediaView {
  id: string;
  field: string;
  kind: "image" | "video" | "document";
  content_type: string;
  size_bytes: number;
  status: string;
  url: string;
}

export interface HistoryItem {
  action: string;
  staff: string | null;
  detail: Record<string, unknown>;
  created_at: string;
}

export interface ReportView {
  id: string;
  reason: string;
  details: string | null;
  status: "open" | "dismissed" | "actioned";
  created_at: string;
  reporter: string | null;
}

export interface ReportItem extends ReportView {
  listing_id: string;
  public_id: string;
  title: string;
  listing_status: Status;
}

export interface ReviewItem {
  id: string;
  listing_id: string;
  public_id: string;
  title: string;
  author: string;
  author_mobile: string | null;
  stars: number;
  tags: string[];
  visited: boolean;
  text: string | null;
  text_status: "pending" | "published" | "rejected" | null;
  reject_reason: string | null;
  removed: boolean;
  updated_at: string;
}

export const REASON_LABEL: Record<string, string> = {
  sold_or_rented: "Already sold or rented",
  fake: "Fake or not a real property",
  wrong_details: "Wrong price or details",
  wrong_photos: "Photos are not of this place",
  spam: "Spam or duplicate",
  fraud: "Asked for money in advance / fraud",
  other: "Something else",
};

export interface ListingDetail extends QueueItem {
  answers: Record<string, unknown>;
  rejection_reason: string | null;
  published_at: string | null;
  media: MediaView[];
  owner: Owner & { ads_total: number; ads_live: number; ads_rejected: number };
  history: HistoryItem[];
  reports: ReportView[];
  views: number;
  contacts: number;
  pending_locality: import("./pages/PendingLocality").PendingLocalityView | null;
  pending_answers?: Record<string, unknown> | null;
  photo_changes?: string[]; // the owner's photo tag / cover changes, in words
  edit_reason?: string | null;
  deleted_from?: Status | null;
  delete_note?: string | null;
  places?: Record<string, string>; // names for the place ids in the answers
}

/** An ad's status in words: the owner's deactivation is not a take-down. */
export const statusText = (ad: { status: Status; deactivated_by?: string | null }) =>
  ad.status === "deactivated" && ad.deactivated_by === "owner" ? "Deactivated by owner" : STATUS_LABEL[ad.status];

export const DELETE_REASON_LABEL: Record<string, string> = {
  sold_here: "Sold / rented through RealRoof",
  sold_elsewhere: "Sold / rented elsewhere",
  not_selling: "No longer available",
  other: "Something else",
};

export interface AuditItem extends HistoryItem {
  target_type: string;
  target_id: string;
}

export const ROLE_LABEL: Record<StaffRole, string> = {
  super_admin: "Super admin",
  admin: "Admin",
  moderator: "Moderator",
  support: "Support (view only)",
};

export const STATUS_LABEL: Record<Status, string> = {
  draft: "Draft",
  under_review: "Waiting review",
  live: "Live",
  rejected: "Needs changes",
  expired: "Expired",
  deactivated: "Taken down",
  deleted: "Deactivated by owner",
};

export const canModerate = (role: StaffRole | undefined) => role === "super_admin" || role === "admin" || role === "moderator";
export const canSeeAudit = (role: StaffRole | undefined) => role === "super_admin" || role === "admin";
export const canManage = canSeeAudit; // admins: plans, coupons, home box ads, the home page

export const NO_ANSWER = "We could not reach RealRoof, or it failed to answer. Check your connection and try again; if it keeps happening, tell the team what you were doing.";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** per-field problems, when the API names them ({field: message}) */
    public readonly fields?: Record<string, string>,
  ) {
    super(message);
  }
}

function fieldsOf(data: unknown): Record<string, string> | undefined {
  const detail = (data as { detail?: unknown } | null)?.detail;
  return detail && typeof detail === "object" && !Array.isArray(detail) ? ((detail as { fields?: Record<string, string> }).fields ?? undefined) : undefined;
}

let onSessionEnd: () => void = () => undefined;
export function whenSessionEnds(fn: () => void) {
  onSessionEnd = fn;
}

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null) {
  try {
    if (token) sessionStorage.setItem(KEY, token);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* private mode: the session lasts until reload */
  }
}

function messageOf(data: unknown): string {
  const detail = (data as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "message" in detail) return String((detail as { message: unknown }).message);
  if (Array.isArray(detail) && detail[0]?.msg) {
    const msg = String(detail[0].msg).replace(/^Value error, /, "");
    return msg.startsWith("value is not a valid email") ? "Enter a valid email address." : msg;
  }
  return "Something went wrong. Please try again.";
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: init.method ?? "GET",
      headers: {
        ...(init.body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    // No answer the browser may read: offline, or a server error (which carries no CORS headers)
    throw new ApiError(0, NO_ANSWER);
  }
  if (res.status === 401 && token) {
    setToken(null);
    onSessionEnd();
  }
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, messageOf(data), fieldsOf(data));
  return data as T;
}

/** Downloads a staff-only file (CSV report) and hands it to the browser as `filename`. */
export async function download(path: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, messageOf(await res.json().catch(() => ({}))));
  const url = URL.createObjectURL(await res.blob());
  Object.assign(document.createElement("a"), { href: url, download: filename }).click();
  URL.revokeObjectURL(url);
}

/** Audit actions in plain words (anything not listed shows as it is). */
export const ACTION_LABEL: Record<string, string> = {
  staff_login: "Logged in",
  pro_verify: "Verified builder / agent",
  pro_reject: "Did not verify builder / agent",
  approve: "Approved ad",
  approve_l1: "Checked ad (level 1)",
  approve_changes_l1: "Checked ad changes (level 1)",
  return_l1: "Returned ad to level 1",
  reject: "Sent ad back",
  take_down: "Took ad down",
  restore: "Restored ad",
  approve_changes: "Approved ad changes",
  reject_changes: "Sent ad changes back",
  report_dismiss: "Dismissed report",
  review_approve: "Approved review",
  review_reject: "Rejected review",
  staff_grant: "Gave staff access",
  staff_role: "Changed staff access",
  staff_revoke: "Removed staff access",
  box_create: "Added home box",
  box_edit: "Changed home box",
  box_delete: "Removed home box",
  home_add: "Added to the home page",
  home_edit: "Changed the home page",
  home_remove: "Removed from the home page",
  coupon_create: "Made coupon",
  credit_grant: "Gave credits",
  pack_create: "Made plan",
  pack_edit: "Changed plan",
  seller_edit: "Changed seller details",
  contact_settings: "Changed number-view settings",
  location_approve: "Approved place",
  location_reject: "Rejected place",
  location_edit: "Changed place",
  location_hide: "Hid place",
  location_unhide: "Showed place",
  top_city_order: "Reordered top cities",
  photo_tag_add: "Added photo tag",
  photo_tag_edit: "Changed photo tag",
  password_change: "Changed own password",
};

/** An audit entry's details on one line. */
export function detailText(detail: Record<string, unknown>): string {
  return Object.entries(detail)
    .filter(([, v]) => v !== null && v !== "")
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(" · ");
}
